<template>
	<div>
		<PaymentMethodButton
			v-if="!isFormVisible"
			testid="ancvPayment"
			icon="mdi-ticket-confirmation-outline"
			:label="AncvLabel"
			:disabled="disabled"
			@click="selectPaymentMethod"
		/>
		<transition name="slide" mode="out-in">
			<v-card v-if="isFormVisible" class="primary defaultBorder">
				<v-form
                    v-model="formStartValid"
                    :id="`ancvCardForm-1`"
                    class="cardForm"
                    :disabled="loading"
                    @submit.prevent="selectPaymentFlow"
                >
					<div class="step">
						<v-card>
							<v-card-title
								data-testId="start-title"
								class="text-left"
							>
								{{ $t("ancv.title") }}
							</v-card-title>
							<v-card-text>
								<p>
									{{ $t("ancv.requirements") }}
									<ul>
										<li><v-icon>mdi-check</v-icon> {{ $t("ancv.requirement.1") }}</li>
										<li><v-icon>mdi-check</v-icon> {{ $t("ancv.requirement.2") }}</li>
										<li><v-icon>mdi-check</v-icon> {{ $t("ancv.requirement.3") }}</li>
									</ul>
								</p>
								<p class="font-weight-bold my-4">{{ $t("ancv.requirements.notes") }}</p>
								<CheckBoxTermsConditions
									:readTerms="hasReadTerms"
									:loading="loading"
								/>
								<v-row
									style="padding: 0 10px 10px"
									:justify="isMobile ? 'center' : 'end'"
								>
									<v-btn
										class="mt-4 cancelBtn"
										color="primary"
										x-large
										text
										@click="cancel"
										outlined
									>
										{{ $t("btn.cancel") }}
									</v-btn>

									<v-btn
										class="mt-4 white--text"
										type="submit"
										color="button"
										:disabled="!canConfirm"
										:loading="loading"
										x-large
									>
										{{ $t("btn.confirm") }}
									</v-btn>
								</v-row>
							</v-card-text>
						</v-card>
					</div>
				</v-form>
			</v-card>
		</transition>
		<PaymentMethodANCVModal
			:isModalOpen="isModalOpen"
			:qr-code="qrCode"
			:is-manual-flow="isManualFlowOnly"
			:is-authorizing-step="isAuthorizingStep"
			:is-polling-timed-out="isPollingTimedOut"
			:loading="loading"
			@start-ancv-process="startAncvProcess"
			@close="closeModal"
		/>
		<PaymentProcessOverlay
			v-if="overlayErrorMessage"
			:error.sync="error"
			:errorMessage="overlayErrorMessage"
			@closed="closeOverlay"
		/>
	</div>
</template>

<script>
import {
	BaseModel,
	CartModel,
	PaymentModel,
	PaymentService,
	EventBus,
} from "@connectngo/sdk";
import WindowWidthMixin from "@/mixins/WindowWidthMixin";
import CheckBoxTermsConditions from "@/components/Inputs/CheckBoxTermsConditions.vue";
import PaymentMethodButton from "@/components/PaymentMethodButton.vue";
import PaymentMethodANCVModal from "@/components/PaymentMethodANCVModal.vue";
import PaymentProcessOverlay from "@/components/PaymentProcessOverlay.vue";

export default {
	name: "PaymentMethodANCV",
	mixins: [WindowWidthMixin],
	components: {
		PaymentMethodButton,
		CheckBoxTermsConditions,
		PaymentMethodANCVModal,
		PaymentProcessOverlay,
	},
	props: {
		selectedMethod: {
			type: String,
			require: true,
		},
		disabled: {
			type: Boolean,
			default: false,
		},
		cart: {
			type: CartModel,
			default: () => new CartModel(),
		},
		payments: {
			type: PaymentModel,
			default: () => new PaymentModel(),
		},
		engine: {
			type: BaseModel,
			default: () => new BaseModel(),
		},
	},
	data: () => ({
		loading: false,
		isModalOpen: false,
		paying: false,
		success: false,
		error: false,
		overlayErrorMessage: '',
		formStartValid: false,
		hasReadTerms: false,
		qrCode: null,
		paymentId: null,
		preTransactionId: null,
		preTransactionStatus: null,
		transactionId: null,
		transactionStatus: null,
		isManualFlowOnly: false,
		statusCheckInterval: null,
		isAuthorizingStep: false,
		isPolling: false,
		pollingTimeout: null,
		isPollingTimedOut: false
	}),
	computed: {
		isFormVisible() {
			return this.selectedMethod === "ancv";
		},
		canConfirm() {
			return this.formStartValid && !this.loading;
		},
		AncvLabel() {
			if(!this.engine.data.name || !this.engine.data.name_i18n) {
				return this.$t("ancv.label");
			}
			else {
				const paymentEngineName = this.$options.filters.translatable(
					// Default to payment engine name if i18n not set for payment engine
					this.engine.data.name,
					this.engine.data.name_i18n,
					this.$i18n.locale
				);

				return this.$options.filters.translatable(
					paymentEngineName,
					this.engine.data?.payment_methods[0]?.name_i18n,
					this.$i18n.locale
				);
			}
		},
	},
	methods: {
		selectPaymentMethod() {
			this.$emit("select-method", "ancv");
		},
		async selectPaymentFlow() {
			this.openModal();

			// Manual flow only on mobile and tablet
			if(this.isMobile || this.isIpad) {
				this.isManualFlowOnly = true;
			}
        },
		payAmount(amount) {
			const amountRemaining = this.payments.data.balance - amount;

			this.cart.data.amountToPay = amountRemaining;
			this.cart.data.balance = amountRemaining;

			EventBus.publish('CART_UPDATED', this.cart);

			this.$snack(this.$t('ancv.applied'));

			if(amountRemaining === 0) {
				const method = {
					paymentMethod: this.engine.data.payment_methods[0]?.name,
					amount
				}
				this.$emit('paymentMethodCaptured', method);
				this.$emit('completed');
			} else {
				this.$emit('partialPaymentCompleted');
			}
		},
		async processAncvPayment(beneficiaryId = null) {
			let response;
			this.loading = true;
			try {
				response = await new PaymentService().processAncvPayment(
					this.engine.data.id,
					this.cart.data.uuid,
					this.cart.data.amountToPay,
					this.$root.websiteConfig.data.pointofsale_id,
					this.$root.websiteConfig.data.salesgroup_id,
					beneficiaryId
				)

				// Check if response contains errors
				if (response.data?.original?.errors || response.data?.original?.message) {
					const errorMessage = response.data.original.message || this.$t('ancv.error.generic');
					this.$snack(errorMessage, 'error');
					this.closeModal();
					return null;
				}
			} catch(error) {
				const { no_balance_left } = error;

				if(no_balance_left) {
					this.error = true;
					this.overlayErrorMessage = this.$t('processPayment.noBalanceLeft');
				} else {
					this.$handleError(this, error, this.formErrors);
				}
				this.closeModal()
				return null;
			} finally {
				this.loading = false
			}
			return response.data
		},
		async getAncvPreTransactionStatus() {
			if(this.isPolling) return;
			try {
				this.isPolling = true;
				const { data } = await new PaymentService().getAncvPreTransactionStatus(
					this.engine.data.id,
					this.preTransactionId,
					this.paymentId
				);
				this.preTransactionStatus = data.state;

				// When the payment is being processed or is in Authorizing step, show loading and continue polling
				if (this.preTransactionStatus === "AUTHORIZING" || this.preTransactionStatus === "PROCESSING") {
					this.isAuthorizingStep = true;
				}

				// When the pre-transaction is aborted (by user or system)
				if (this.preTransactionStatus === "ABORTED") {
					this.closeModal();
				}

				if (this.preTransactionStatus === "USED") {
					this.closeModal();
					this.payAmount(data.amountPaid);
				}
			} catch(error) {
				this.$handleError(this, error);
				this.stopStatusPolling();
				this.closeModal();
			} finally {
				this.isPolling = false;
			}
		},
		async getAncvTransactionStatus() {
			if(this.isPolling) return;
			try {
				this.isPolling = true;
				const { data } = await new PaymentService().getAncvTransactionStatus(
					this.engine.data.id,
					this.transactionId,
					this.paymentId
				);
				this.transactionStatus = data.state;

				if (this.transactionStatus === "PROCESSING") {
					this.isAuthorizingStep = true;
				}

				if (this.transactionStatus === "ABORTED") {
					this.closeModal();
				}

				if (this.transactionStatus === "VALIDATED") {
					this.closeModal();
					this.payAmount(data.amountPaid);
				}
			} catch(error) {
				this.$handleError(this, error);
				this.stopStatusPolling();
				this.closeModal();
			} finally {
				this.isPolling = false;
			}
		},
		async handleGetStatus(isPreStatus = false) {
			// Clear any existing interval
			this.stopStatusPolling();

			const getTransactionStatus = isPreStatus ? this.getAncvPreTransactionStatus : this.getAncvTransactionStatus;

			// Check immediately
			await getTransactionStatus();

			// Start polling every 5 seconds
			this.statusCheckInterval = setInterval(() => {
				getTransactionStatus();
			}, 5000);

			// Set timeout for 2 minutes (120000ms)
			const timeoutDuration = 120000;  // 2 minutes
			this.pollingTimeout = setTimeout(() => {
				this.isPollingTimedOut = true;
				this.stopStatusPolling();
				console.log('ANCV polling timeout reached - stopped polling');
			}, timeoutDuration);
		},
		stopStatusPolling() {
			if (this.statusCheckInterval) {
				clearInterval(this.statusCheckInterval);
				this.statusCheckInterval = null;
			}
			if (this.pollingTimeout) {
				clearTimeout(this.pollingTimeout);
				this.pollingTimeout = null;
			}
		},
		async removePayment() {
			try {
				await new PaymentService().voidCashlessPayment(this.paymentId)
			} catch(error) {
				this.$handleError(this, error);
			}
		},
		async startAncvProcess(beneficiaryId = null) {
			// Cancel previous payment process in progress
			if(this.paymentId) {
				this.stopStatusPolling();
				await this.removePayment()
			}
			// Start new payment process
			const ancv = await this.processAncvPayment(beneficiaryId)
			if(!ancv) return;

			this.paymentId = ancv.paymentId

			// If beneficiaryId is provided, we are in manual flow
			if (beneficiaryId) {
				this.transactionId = ancv.data?.transaction?.id
			} else {
				this.qrCode = ancv.qrCode
				this.preTransactionId = ancv.preTransactionId
			}

			const isPreStatus = !beneficiaryId;
			this.handleGetStatus(isPreStatus)
		},
		resetValues() {
			this.isModalOpen = false;
			this.qrCode = null;
			this.preTransactionId = null;
			this.preTransactionStatus = null;
			this.transactionId = null;
			this.transactionStatus = null;
			this.isManualFlowOnly = false;
			this.isAuthorizingStep = false;
			this.isPollingTimedOut = false;
		},
		openModal() {
			this.isModalOpen = true;
		},
		closeModal() {
			this.stopStatusPolling();
			this.resetValues();
		},
		closeOverlay() {
			this.error = false;
			this.overlayErrorMessage = '';
			this.$router.push({ name: 'home' });
		},
		cancel() {
			this.$emit("cancel", null);
			this.closeModal();
		},
	},
	beforeDestroy() {
		// Clean up the interval when component is destroyed
		this.stopStatusPolling();
	},
};
</script>

<style lang="scss" scoped>
.slide-enter-active,
.slide-leave-active {
	transition: all 1s ease;
}

.slide-enter,
.slide-leave-to {
	max-height: 0;
	opacity: 0;
	overflow: hidden;
}

.defaultBorder {
	border: 2px solid;
}

.step {
	p {
		font-size: 1rem;
		ul {
			list-style: none;
		}
	}


	.v-card__title,
	label {
		color: #383838;
	}

	button {
		min-width: 150px;
		height: 48px;
		padding: 0 20px;
		text-transform: none;
	}

	@media (max-width: 600px) {
		button {
			width: 100%;
		}
	}

	.v-card__title,
	button,
	label {
		font-weight: 600;
		font-size: 18px;
	}

	button:not(:last-child) {
		margin-right: 10px;
	}

	.cancelBtn {
		border-style: solid;
		border-width: 1px;
		border-color: inherit !important;
	}
}

@media (max-width: 600px) {
	.cardForm {
		button:not(:last-child) {
			margin-right: 0;
		}
	}
}
</style>
